import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import { Link } from "react-router-dom";
import MoodboardHero from "@/components/MoodboardHero";
import { useReveal } from "@/hooks/useReveal";
import envOffice from "@/assets/env-office.webp";
import envRestaurant from "@/assets/env-restaurant.webp";
import envVantrum from "@/assets/env-vantrum.webp";
import envFrukost from "@/assets/env-frukost.webp";
import dispenserHero from "@/assets/dispenser-hero.webp";

const environments = [
  { title: "Kontoret", desc: "Placera dispensern i köket eller vid kaffemaskinen, där kollegorna samlas.", image: envOffice },
  { title: "Restaurangen", desc: "Erbjud gästerna en extra touch av hälsa vid dryckestationen.", image: envRestaurant },
  { title: "Väntrummet", desc: "Ge besökare och kunder en positiv upplevelse redan vid ankomst.", image: envVantrum },
  { title: "Frukosten", desc: "Komplettera frukosten med vitaminer, perfekt för hotell och konferensanläggningar.", image: envFrukost },
];

const steps = [
  { step: "01", title: "Välj paket", desc: "Vi hjälper dig hitta rätt upplägg för din arbetsplats." },
  { step: "02", title: "Vi levererar", desc: "Dispensern levereras med beställt mängd brustabletter." },
  { step: "03", title: "Fyll på enkelt", desc: "Prenumerera på refills eller beställ vid behov. Vi skickar direkt till dörren." },
];

const RevealDiv = ({
  children,
  delay = 0,
  fromRight = false,
  className = "",
}: {
  children: React.ReactNode;
  delay?: number;
  fromRight?: boolean;
  className?: string;
}) => {
  const { ref, className: revealClass } = useReveal({ fromRight });
  return (
    <div ref={ref} className={`${revealClass} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const FadeUp = ({
  children,
  delay = 0,
  className = "",
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => {
  const { ref, visible } = useReveal();
  return (
    <div
      ref={ref}
      className={`fade-up ${visible ? "visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const TjansterPage = () => {
  return (
    <Layout>
      <Seo
        title="Vår tjänst – Aquadias dispenser och leverans"
        description="Så fungerar Aquadias: dispenser, prenumeration på brustabletter och enkel påfyllning för kontor, restauranger och väntrum."
        path="/tjanster"
      />
      <MoodboardHero />
      {/* How it works */}
      <section id="sa-fungerar" className="px-6 md:px-12 py-20 md:py-28">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <RevealDiv>
            <img src={dispenserHero} alt="Aquadias vitamindispenser" className="w-full rounded-2xl shadow-lg" loading="lazy" />
          </RevealDiv>
          <RevealDiv fromRight delay={150} className="space-y-6">
            <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tight text-foreground">Så fungerar det</h2>
            <div className="space-y-4">
              {steps.map((item, i) => (
                <FadeUp key={item.step} delay={300 + i * 120} className="flex gap-4">
                  <span className="text-2xl font-black text-terracotta-400">{item.step}</span>
                  <div>
                    <p className="font-bold text-foreground">{item.title}</p>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </RevealDiv>
        </div>
      </section>

      {/* Environments */}
      <section className="bg-cream-50 px-6 md:px-12 py-20 md:py-28">
        <div className="max-w-6xl mx-auto">
          <FadeUp className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tight text-foreground mb-4">Perfekt för alla miljöer</h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              Dispensern passar överallt där människor samlas, och där hälsa gör skillnad.
            </p>
          </FadeUp>
          <div className="grid md:grid-cols-2 gap-6">
            {environments.map((env, i) => (
              <FadeUp key={env.title} delay={i * 120}>
                <div className="relative rounded-2xl overflow-hidden aspect-[4/3] group">
                  <img src={env.image} alt={env.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-foreground/30 group-hover:bg-foreground/40 transition-colors" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-xl font-bold text-white mb-1">{env.title}</h3>
                    <p className="text-sm text-white/80">{env.desc}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing models */}
      <section className="px-6 md:px-12 py-20 md:py-28">
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
          <RevealDiv className="bg-gradient-to-br from-coral-400/20 to-cream-100 rounded-2xl p-8 md:p-10">
            <h3 className="text-xl font-bold uppercase tracking-wider text-foreground mb-3">Prenumeration</h3>
            <p className="text-muted-foreground text-sm leading-relaxed mb-6">
              Automatisk leverans av brustabletter varje månad. Ingen bindningstid. Pausa eller avsluta när som helst.
            </p>
            <Link to="/pricing" className="text-sm font-medium text-foreground tracking-wide hover:underline">SE PRISER →</Link>
          </RevealDiv>
          <RevealDiv fromRight delay={150} className="bg-gradient-to-br from-terracotta-100 to-cream-100 rounded-2xl p-8 md:p-10">
            <h3 className="text-xl font-bold uppercase tracking-wider text-foreground mb-3">Köp vid behov</h3>
            <p className="text-muted-foreground text-sm leading-relaxed mb-6">
              Beställ refills när det passar dig. Perfekt om du vill testa eller har oregelbundet behov.
            </p>
            <Link to="/pricing" className="text-sm font-medium text-foreground tracking-wide hover:underline">SE PRISER →</Link>
          </RevealDiv>
        </div>
      </section>
    </Layout>
  );
};

export default TjansterPage;
